import { useCallback, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import { extractErrorMessage, isSessionExpired } from '../../api/apiErrors';
import { fetchCompanyConflictCount } from '../../api/integrationCompanies';
import type { ConflictsItem, ImportWizardState } from '../types';

type ApiContext = {
  baseUrl: string;
  nonce: string;
};

type UseConflictsSummaryParams = {
  api: ApiContext;
  onSessionExpired: () => void;
  getState: () => ImportWizardState;
  setState: React.Dispatch<React.SetStateAction<ImportWizardState>>;
};

const CONFLICTS_CONCURRENCY = 4;

const runConcurrent = async <T>(
  tasks: (() => Promise<T>)[],
  concurrency: number,
): Promise<T[]> => {
  const results: T[] = new Array(tasks.length);
  let nextIndex = 0;
  const active = new Set<Promise<void>>();

  const launch = (index: number) => {
    const p = tasks[index]().then((result) => {
      results[index] = result;
    }).finally(() => {
      active.delete(p);
    });
    active.add(p);
  };

  while (nextIndex < tasks.length || active.size > 0) {
    while (active.size < concurrency && nextIndex < tasks.length) {
      launch(nextIndex);
      nextIndex += 1;
    }
    if (active.size > 0) {
      await Promise.race(active);
    }
  }

  return results;
};

export const useConflictsSummary = ({
  api,
  onSessionExpired,
  getState,
  setState,
}: UseConflictsSummaryParams) => {
  const [conflictsError, setConflictsError] = useState('');

  const loadConflictsSummary = useCallback(async () => {
    setConflictsError('');
    setState((prev) => ({
      ...prev,
      conflicts: { ...prev.conflicts, status: 'loading' },
    }));

    try {
      // For server-side jobs, collect company IDs from the run items in wizard state
      // (mirrored back by useImportJob when job finishes).
      const companyIds = Array.from(
        new Set(
          getState()
            .run.items.filter((item) => item.status === 'success')
            .map((item) => item.targetCompanyId)
            .filter((id) => id > 0),
        ),
      );

      if (companyIds.length === 0) {
        setState((prev) => ({
          ...prev,
          conflicts: { status: 'loaded', items: [] },
        }));
        return;
      }

      const tasks = companyIds.map(
        (id) => async (): Promise<ConflictsItem> => {
          try {
            const totalItems = await fetchCompanyConflictCount(api, id);
            return { companyId: id, totalItems, status: 'loaded' };
          } catch (error) {
            if (isSessionExpired(error)) {
              onSessionExpired();
            }
            return { companyId: id, totalItems: null, status: 'unknown' };
          }
        },
      );

      const results = await runConcurrent(tasks, CONFLICTS_CONCURRENCY);

      setState((prev) => ({
        ...prev,
        conflicts: { status: 'loaded', items: results },
      }));
    } catch (error) {
      setConflictsError(extractErrorMessage(error, __('Lecture conflits impossible.')));
      setState((prev) => ({
        ...prev,
        conflicts: { ...prev.conflicts, status: 'loaded' },
      }));
    }
  }, [api, getState, onSessionExpired, setState]);

  return { conflictsError, loadConflictsSummary };
};
